"use client";

import React from "react";
import clsx from "clsx";
import { Tooltip } from "@jasperlabs/ui";

const DefaultWrapper = React.forwardRef<
	HTMLDivElement,
	{
		children: React.ReactNode;
		className?: string;
	} & React.HTMLProps<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
	<div ref={ref} className={className} {...props}>
		{children}
	</div>
));

DefaultWrapper.displayName = "NoPermissionElementDefaultWrapper";

export type DisableableElement = React.ReactElement<{
	disabled?: boolean;
	"aria-disabled"?: boolean;
	children?: React.ReactNode;
	className?: string;
	fullWidth?: boolean;
}>;

function getWrapperClassName(children: DisableableElement, className?: string) {
	const childClassName =
		typeof children.props.className === "string"
			? children.props.className
			: "";
	const childWidthClassName = children.props.fullWidth ? "w-full" : "";

	return clsx(childWidthClassName, childClassName, className);
}

function disable(
	element: DisableableElement,
	props: { children?: React.ReactNode } = {},
) {
	return React.cloneElement(element, {
		disabled: true,
		"aria-disabled": true,
		className: clsx(element.props.className, "pointer-events-none"),
		...props,
	});
}

function disableDeep(children: React.ReactNode): React.ReactNode {
	return React.Children.map(children, (child) => {
		if (!React.isValidElement(child)) {
			return child;
		}

		const disableableChild = child as DisableableElement;

		if (disableableChild.props.children) {
			return disable(disableableChild, {
				children: disableDeep(disableableChild.props.children),
			});
		}

		return disable(disableableChild);
	});
}

export type NoPermissionElementProps = {
	children: DisableableElement;
	wrapWith?: React.ComponentType<{
		children: React.ReactNode;
		className?: string;
	}>;
	className?: string;
	noPermissionHint?: React.ReactNode;
	/**
	 * Whether to explain the disabled state in a tooltip. Set to `false` when
	 * the caller already prints the reason somewhere always visible, so the
	 * same sentence isn't shown twice — and so the tooltip can't cover the
	 * control that clears the condition. The element stays disabled either way.
	 */
	showHint?: boolean;
};

export function NoPermissionElement({
	wrapWith: Wrapper = DefaultWrapper,
	noPermissionHint = "You don't have permission to perform this action.",
	className,
	showHint = true,
	children,
}: NoPermissionElementProps) {
	const wrapperClassName = getWrapperClassName(children, className);
	const disabledChildren = (
		<Wrapper className={wrapperClassName}>{disableDeep(children)}</Wrapper>
	);

	if (!showHint) {
		return disabledChildren;
	}

	return (
		<Tooltip content={noPermissionHint} asChild>
			{disabledChildren}
		</Tooltip>
	);
}

type NoPermissionElementIfConditionProps = NoPermissionElementProps & {
	condition: boolean;
};

export function NoPermissionElementIfCondition({
	condition,
	children,
	...props
}: NoPermissionElementIfConditionProps) {
	if (condition) {
		return <NoPermissionElement {...props}>{children}</NoPermissionElement>;
	}

	return children;
}
